import type { SubscriptionTypes } from "@component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { Config } from "@config";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import { http } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { evaluateEligibilityForLoan } from "./domain";
import type {
	FormFields,
	WorkerLoanLeadCalcRequest,
	WorkerLoanLeadCalcResponse,
	WorkerLoanRequest,
	WorkerLoanResponse,
} from "./types";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	leadformResponse: WorkerLoanResponse;
	eligibleForWorkerLoan: boolean;
}

/**
 * @Shortcode
 *
 * @title "Munkáshitel"
 * @shortcode "worker-loan"
 * @slug "munkashitel"
 * @type "calculator"
 */
export class WorkerLoanSmallCalculator extends AbstractLeadForm<
	WorkerLoanLeadCalcRequest,
	WorkerLoanLeadCalcResponse,
	FormFields,
	AbstractLeadFormProps<WorkerLoanLeadCalcRequest, WorkerLoanLeadCalcResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "WORKER_LOAN";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "",
		outsideTitleLink: "",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			leadformResponse: undefined,
			eligibleForWorkerLoan: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			eligibleForWorkerLoan,
			submitState,
			values: { email },
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" />
				<Step.Step2 stepName="step-2" defaultTarget="lead-form" canStepBack />
				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget="success-page-subscription-form"
					eligible={eligibleForWorkerLoan ? "jogosult lehetsz" : "nem vagy jogosult"}
					canStepBack
				/>
				<Step.SubscriptionForm
					stepName="subscription-form"
					defaultTarget="success-page"
					canStepBack
					eligibleForWorkerLoan={eligibleForWorkerLoan}
				/>
				<Step.SuccessPage stepName="success-page" eligibleForWorkerLoan={eligibleForWorkerLoan} />
				<Step.SuccessPageSubscriptionForm
					stepName="success-page-subscription-form"
					userEmail={email}
					subscriptionTypes={["RETAIL_NEWSLETTER"]}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "lead-form":
				this.onLeadFormStep(target, withEvent);
				break;

			case "success-page-subscription-form":
				this.onSuccessPage();
				super.onStep(target, withEvent);
				break;

			case "subscription-form":
				this.onSubscriptionForm();
				super.onStep(target, withEvent);
				break;

			default:
				super.onStep(target, withEvent);
		}
	}

	private onLeadFormStep(target: string, withEvent: boolean): void {
		evaluateEligibilityForLoan(this.getCalculatorRequest()).then((eligibleForWorkerLoan) => {
			this.setState({
				eligibleForWorkerLoan,
			});
			super.onStep(eligibleForWorkerLoan ? target : "subscription-form", withEvent);
		});
	}

	private onSubscriptionForm(): void {
		const {
			eligibleForWorkerLoan,
			values: { wantsNewsletter, firstName, lastName, email },
		} = this.state;

		const subscriptionTypes: SubscriptionTypes[] = wantsNewsletter ? ["RETAIL_NEWSLETTER"] : [];
		const tag = wantsNewsletter ? "Munkáshitel, Összes" : "Munkáshitel";

		this.setState({
			submitState: "loading",
		});

		if (wantsNewsletter) {
			this.sendEvent("BM_newsletterSubscription", [
				{ koMessage: eligibleForWorkerLoan ? "Jogosult" : "Nem jogosult", firstName, lastName, email },
			]);
		}

		this.promisePool
			.add(
				Promise.all(
					[
						http.post(
							Config.getWpRestApiUrl(`/autoresponder/${encodeURIComponent(this.getAutoresponderName())}`),
							{
								email,
								fields: this.getAutoresponderFields(),
							},
						),
						http.post(Config.getWpRestApiUrl(`/subscribe/${encodeURIComponent(email)}`), {
							subscriptionTypes,
							firstName,
							tags: [tag],
						}),
					].filter(Boolean),
				),
			)
			.then(() => {
				super.onStep("success-page", false);
				this.setState({
					submitState: "done",
				});
			})
			.catch(() => {
				this.setState({
					submitState: "error",
				});
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});
			});
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});

		this.sendLead()
			.then(() => {
				this.setState({
					submitState: "done",
				});
				super.onStep("success-page-subscription-form", true);
			})
			.catch((error: Error) => {
				logError(error);
			});
	}

	protected getCalculatorRequest(): WorkerLoanRequest {
		const {
			values: {
				loanAmount,
				maturityMonths,
				monthlyIncome,
				hasHungarianEmploymentOrEntrepreneurRelationship,
				age,
				isAttendingHigherEducation,
				hasHigherEducationQualification,
				has3MonthsLegalHealthInsurance,
				hasMoreThan5kTaxDebt,
				hasHungarianAddress,
				isOnBadDebtorsList,
			},
		} = this.state;

		return {
			...super.getCalculatorRequest(),
			applicantAge: age,
			hasHungarianEmploymentOrEntrepreneurRelationship,
			isAttendingHigherEducation,
			hasHigherEducationQualification,
			hasHungarianAddress,
			has3MonthsLegalHealthInsurance,
			hasMoreThan5kTaxDebt,
			hasOrWantsRetailAccountOpening: true,
			incomeList: [
				{
					incomeType: "SALARY-H",
					amount: monthlyIncome,
					durationMonths: 36,
					receivedOnBankAccount: true,
					incomeStart: "2023-01-01",
					probationaryPeriodEnded: true,
					employmentType: "INDEFINITE",
				},
			],
			khrStatus: isOnBadDebtorsList ? "ACTIVE" : "GOOD",
			loanAmount,
			existingLoans: [],
			maturityMonths,
			bankCodes: ["cib", "erste", "kh", "mbh", "raiffeisen"],
			roleCode: "BM",
		};
	}

	protected getLeadData(): LeadRequest<WorkerLoanLeadCalcRequest, WorkerLoanLeadCalcResponse> {
		const {
			values: {
				loanAmount,
				maturityMonths,
				monthlyIncome,
				hasHungarianEmploymentOrEntrepreneurRelationship,
				age,
				isAttendingHigherEducation,
				hasHigherEducationQualification,
				has3MonthsLegalHealthInsurance,
				hasMoreThan5kTaxDebt,
				hasHungarianAddress,
				isOnBadDebtorsList,
				requestConsultantCall,
			},
		} = this.state;
		const data = super.getLeadData();
		const request = this.getCalculatorRequest();
		const otherData = new JsonDataBuilder(request.otherDataJson);

		otherData.addItem(
			"Van magyar alkalmazotti vagy vállalkozói jogviszonya",
			"hasHungarianEmploymentOrEntrepreneurRelationship",
			hasHungarianEmploymentOrEntrepreneurRelationship,
			"bool",
		);
		otherData.addItem(
			"Felsőoktatási intézmény tanulója",
			"isAttendingHigherEducation",
			isAttendingHigherEducation,
			"bool",
		);
		otherData.addItem(
			"Van felsőoktatási, egyetemi végzettsége",
			"hasHigherEducationQualification",
			hasHigherEducationQualification,
			"bool",
		);
		otherData.addItem(
			"Van legalább 3 hónapos TB jogviszonya",
			"has3MonthsLegalHealthInsurance",
			has3MonthsLegalHealthInsurance,
			"bool",
		);
		otherData.addItem("Van legalább 5 ezer Ft adótartozása", "hasMoreThan5kTaxDebt", hasMoreThan5kTaxDebt, "bool");
		otherData.addItem("Van magyarországi lakcíme", "hasHungarianAddress", hasHungarianAddress, "bool");
		otherData.addItem("Szerepel a rossz adósok listáján", "isOnBadDebtorsList", isOnBadDebtorsList, "bool");

		return {
			...data,
			request: {
				...request,
				otherDataJson: otherData.getJson(),
				age,
				loanAmount,
				maturityMonths,
				requestConsultantCall,
				incomeList: [
					{
						incomeType: "SALARY-H",
						incomeTypeName: "Magyar alkalmazotti jövedelem",
						amount: monthlyIncome,
						receiptAmount: monthlyIncome,
					},
				],
			},
		};
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName },
		} = this.state;

		return {
			lastName,
			firstName,
		};
	}
	protected getAutoresponderName(): string {
		return "autoresponder-worker-loan";
	}

	protected getProductType(): ProductType {
		return "WORKER_LOAN";
	}

	protected getComponentName(): string {
		return "WorkerLoanSmallCalculator";
	}
}

export default WorkerLoanSmallCalculator;
